import type { ReactNode } from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';

/** Column width and gutter, shared so keyboard scrolling can step exactly one column. */
export const COLUMN_WIDTH = 320;
export const COLUMN_GAP = 16;

export interface KanbanColumnModel {
  id: number;
  name: string;
  order: number;
  /**
   * Serialized from a Django `CharField(blank=True)`, so an unset colour
   * arrives as `''` and never as `null`. Everything downstream therefore falls
   * back with `||`, not `??` — see `KanbanColumn`.
   */
  color?: string;
  is_done?: boolean;
  wip_limit?: number | null;
}

export interface KanbanBoardModel {
  id: number;
  name: string;
  color?: string;
  is_default?: boolean;
}

/**
 * Payload attached to every droppable and sortable.
 *
 * A discriminated union rather than parsing ids back out of strings: both
 * legacy boards do string surgery on the drop target, which silently
 * misidentifies anything whose id happens to contain the delimiter.
 *
 * `columnId` and `zoneId` here are the raw caller-side ids. Only the droppable
 * `id` string is namespaced with `boardScope`, so two boards mounted at once
 * cannot collide.
 */
export type KanbanDragData =
  | { type: 'column'; boardScope: string; columnId: number }
  | { type: 'card'; boardScope: string; columnId: number; cardId: UniqueIdentifier }
  | { type: 'zone'; boardScope: string; zoneId: string };

export interface KanbanDropZone<TCard> {
  /** Raw id — namespaced only when it becomes a droppable id. */
  id: string;
  label: string;
  icon: ReactNode;
  color: 'success' | 'danger' | 'warning' | 'neutral' | 'primary';
  onDrop: (card: TCard) => void | Promise<void>;
}

export interface KanbanMoveArgs<TCard> {
  card: TCard;
  fromColumnId: number | null;
  toColumnId: number;
  /**
   * Zero-based index in the target column *as rendered*, with the dragged card
   * excluded. The caller maps it to whatever ordering field the API uses.
   */
  toIndex: number;
}
